/**
 * 맥스매치 Lite 목록 — 레거시 `maxMatchMini.js` 포팅.
 * RDS: PageHeader → FilterCard → DataCard.
 */
import {
  useDownloadMaxMatchMiniExcel,
  useMaxMatchMiniList,
} from '@/entities/max-match/hooks/useMaxMatch';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { formatRefYm } from '@/pages/fee-rates/feeRateUtils';
import { formatDateTime } from '@/shared/lib/dateFormat';
import {
  downloadXlsx,
  MAX_MATCH_LITE_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Download, RefreshCw, Search, Sparkles } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { mapMaxMatchLiteExcelRows } from '@/pages/max-match/maxMatchExcelMapper';
import { saveMaxMatchMiniHeader } from '@/pages/max-match/maxMatchMiniHeader';
import type { MaxMatchMiniItem } from '@/entities/max-match/api/max-match.api';
import {
  isMaxMatchMiniSettledStatus,
  maxMatchApplicationStatusLabel,
} from '@/pages/max-match/maxMatchReviewStatus';

const getSelectableYearMonths = (): string[] => {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(
      `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`,
    );
  }
  return months;
};

const statusLabel = (status: string): string =>
  maxMatchApplicationStatusLabel(status);

const MAX_MATCH_MINI_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  yearMonth: '',
  searchKeyword: '',
  isRequest: false,
};

export const MaxMatchMiniPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const yearMonths = useMemo(() => getSelectableYearMonths(), []);

  const { values, setValues } = useListSearchParams({
    ...MAX_MATCH_MINI_SEARCH_DEFAULTS,
    yearMonth: yearMonths[0] ?? '',
  });

  const [yearMonth, setYearMonth] = useState(values.yearMonth);
  const [keyword, setKeyword] = useState(values.searchKeyword);

  React.useEffect(() => {
    setYearMonth(values.yearMonth);
    setKeyword(values.searchKeyword);
  }, [values.yearMonth, values.searchKeyword]);

  const search = React.useMemo(
    () => ({
      yearMonth: values.yearMonth,
      searchKeyword: values.searchKeyword || undefined,
      isRequest: values.isRequest ? true : undefined,
    }),
    [values.yearMonth, values.searchKeyword, values.isRequest],
  );

  const pageable = React.useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error, refetch, isFetching } =
    useMaxMatchMiniList(search, pageable);
  const downloadMini = useDownloadMaxMatchMiniExcel();

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      yearMonth,
      searchKeyword: keyword.trim(),
      page: 1,
    });
  };

  const handleDownload = async (summaryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await downloadMini.mutateAsync(summaryId);
      downloadXlsx(
        mapMaxMatchLiteExcelRows(result),
        `맥스매치_라이트_${summaryId}`,
        MAX_MATCH_LITE_EXCEL_HEADER_MAP,
      );
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const goDetail = (row: MaxMatchMiniItem) => {
    const header = {
      email: row.email ?? '',
      companyName: row.companyName ?? '',
      // 레거시: createdAt ← maxMaxMatchSummaryCreatedAt
      createdAt: row.maxMaxMatchSummaryCreatedAt ?? row.createdDate ?? '',
      // 레거시: maxMatchCreatedAt ← maxMatchMiniCreatedAt
      maxMatchCreatedAt: row.maxMatchMiniCreatedAt ?? '',
      prescriptionYearMonth: row.prescriptionYearMonth ?? '',
      status: row.status ?? '',
    };
    // search가 유실돼도 상세 상단이 채워지도록 로컬에 보관
    saveMaxMatchMiniHeader(row.summaryId, header);
    navigate({
      to: ROUTE_PATHS.MAX_MATCH_MINI_DETAIL,
      params: { summaryId: String(row.summaryId) },
      search: header,
    } as never);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치 Lite"
        description="맥스매치 Lite 처방 확인 요청 및 결과 조회"
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label>등록년월</Label>
            <Select
              value={yearMonth}
              onValueChange={(value) => {
                setYearMonth(value);
                setValues({ yearMonth: value, page: 1 });
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearMonths.map((ym) => (
                  <SelectItem key={ym} value={ym}>
                    {formatRefYm(ym)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="mini-keyword">검색어</Label>
            <Input
              id="mini-keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="업체명 또는 사용자 이메일"
              className="w-56"
            />
          </div>
          <div className="flex items-end gap-2 pb-0.5">
            <div className="flex items-center gap-2">
              <Checkbox
                id="requestOnly"
                checked={values.isRequest}
                onCheckedChange={(checked) =>
                  setValues({ isRequest: checked === true, page: 1 })
                }
              />
              <Label htmlFor="requestOnly">확인 요청만 보기</Label>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="새로고침"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="맥스매치 Lite 목록을 불러오는 중..." />
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 맥스매치 Lite 항목이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>업체명</TableHead>
                  <TableHead>병의원</TableHead>
                  <TableHead>확정/처방 이미지 수</TableHead>
                  <TableHead>진행 상태</TableHead>
                  <TableHead>등록일시</TableHead>
                  <TableHead>max match</TableHead>
                  <TableHead>결과</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isUnconfirmed = !isMaxMatchMiniSettledStatus(
                    row.status,
                  );
                  return (
                    <TableRow
                      key={row.summaryId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => goDetail(row)}
                    >
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.companyName || '-'}</TableCell>
                      <TableCell>{row.clinicName}</TableCell>
                      <TableCell
                        className={
                          isUnconfirmed ? 'text-destructive' : undefined
                        }
                      >
                        {row.confirmedImageCount ?? 0} /{' '}
                        {row.totalImageCount ?? 0}
                      </TableCell>
                      <TableCell
                        className={
                          isUnconfirmed ? 'text-destructive' : undefined
                        }
                      >
                        {statusLabel(row.status)}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(
                          row.maxMaxMatchSummaryCreatedAt || row.createdDate,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(row.maxMatchMiniCreatedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="ruda-button"
                          disabled={
                            row.status !== 'MATCHED' || downloadMini.isPending
                          }
                          onClick={(e) => handleDownload(row.summaryId, e)}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          다운
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
